import React from 'react';
import type { ToastProps } from '@/components/ui/toast';

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1000;

interface ToastInput extends Omit<ToastProps, 'open' | 'onOpenChange' | 'title'> {
  description?: React.ReactNode;
  title?: React.ReactNode;
}

interface ToastItem extends ToastInput {
  id: string;
  onOpenChange?: ToastProps['onOpenChange'];
  open: boolean;
}

interface ToastState {
  toasts: ToastItem[];
}

type ToastAction =
  | { type: 'ADD_TOAST'; toast: ToastItem }
  | { type: 'DISMISS_TOAST'; toastId: string }
  | { type: 'REMOVE_TOAST'; toastId: string };

let count = 0;
let memoryState: ToastState = { toasts: [] };
const listeners: Array<(state: ToastState) => void> = [];
const removalTimers = new Map<string, number>();

function reducer(state: ToastState, action: ToastAction): ToastState {
  switch (action.type) {
    case 'ADD_TOAST':
      return { toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT) };
    case 'DISMISS_TOAST':
      return {
        toasts: state.toasts.map((item) =>
          item.id === action.toastId ? { ...item, open: false } : item,
        ),
      };
    case 'REMOVE_TOAST':
      return { toasts: state.toasts.filter((item) => item.id !== action.toastId) };
    default:
      return state;
  }
}

function dispatch(action: ToastAction): void {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => listener(memoryState));
}

function dismiss(toastId: string): void {
  dispatch({ type: 'DISMISS_TOAST', toastId });
  if (removalTimers.has(toastId)) return;
  removalTimers.set(
    toastId,
    window.setTimeout(() => {
      removalTimers.delete(toastId);
      dispatch({ type: 'REMOVE_TOAST', toastId });
    }, TOAST_REMOVE_DELAY),
  );
}

function toast(input: ToastInput): { dismiss: () => void; id: string } {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  const id = String(count);
  dispatch({
    type: 'ADD_TOAST',
    toast: {
      ...input,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss(id);
      },
    },
  });
  return { id, dismiss: () => dismiss(id) };
}

function useToast(): ToastState & { dismiss: typeof dismiss; toast: typeof toast } {
  const [state, setState] = React.useState<ToastState>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index >= 0) listeners.splice(index, 1);
    };
  }, []);

  return { ...state, dismiss, toast };
}

export { toast, useToast };
